import { getCompanyDomain } from '@aibees/elephant/components/company-details/utils';
import { CompanyProfileDetailPopover } from '@aibees/elephant/components/company-profile-detail-popover';
import { CurrentCompanyContactData } from '@aibees/elephant/components/current-company-contact-data';
import { useCompanyReadDataContext } from '@aibees/elephant/context';
import {
  Grid2,
  Icon,
  InternalLinkButton,
  TextEllipsis,
} from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Avatar,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { useCurrentCompanyData } from './hooks/use-current-company-data';
import * as Styled from './styles';
import type { CompanySummaryProps as Props } from './types';
import { SocialLink } from '@aibees/elephant/components/search-social-media-item/types';
import { SearchSocialMedia } from '@aibees/elephant/components';
import { formatCompanyLocation } from '@aibees/elephant/utils';
import { useRecordLastUpdate } from '@aibees/elephant/hooks';

export const CompanySummary = memo(({ className }: Props) => {
  const theme = useTheme();
  const { api } = useCompanyReadDataContext();
  const { fullProfileLink } = useCurrentCompanyData();
  const { lastUpdateCompany } = useRecordLastUpdate();

  const domainUrl = useMemo(() => {
    return getCompanyDomain(api?.data?.link);
  }, [api?.data?.link]);

  return (
    <Styled.Wrapper className={classnames('CompanySummary', className)}>
      <Grid2 container columns={48} p={1.5} columnGap={1}>
        <Grid2 xs={9} justifyContent="center" alignItems="center">
          {api?.isLoading ? (
            <Skeleton
              animation="wave"
              variant="rounded"
              width={56}
              height={56}
            />
          ) : (
            <Avatar
              color="white"
              variant="rounded"
              src={api?.data?.summary?.logo?.source ?? undefined}
              size={80}
            >
              <Icon size={30} name={'business'} color="grey.400" />
            </Avatar>
          )}
        </Grid2>
        <Grid2 xs={38}>
          <Stack gap={0.5} alignItems="flex-start" width="100%">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <InternalLinkButton to={fullProfileLink}>
                <TextEllipsis
                  value={api?.data?.summary?.name}
                  variant="body1"
                  fontWeight={700}
                  w={'320px'}
                  color="grey.700"
                />
              </InternalLinkButton>
              <CompanyProfileDetailPopover companyId={api?.data?.id} />
            </Stack>
            <Stack direction="row" gap={0.5}>
              <SearchSocialMedia
                socialLink={api?.data?.link as SocialLink}
                hideLinkOutIcon={false}
              />
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5} width="100%">
              <Stack>
                <Icon
                  name="network"
                  size={16}
                  color={domainUrl ? 'grey.700' : 'grey.400'}
                />
              </Stack>
              <TextEllipsis
                value={domainUrl ?? 'Domain URL Unavailable'}
                variant="body4"
                fontWeight={400}
                color={domainUrl ? 'grey.700' : 'grey.400'}
              />
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5} width="100%">
              <Stack>
                <Icon
                  name="location"
                  size={18}
                  color={
                    api?.data?.location?.headquarter ? 'grey.700' : 'grey.400'
                  }
                />
              </Stack>
              <TextEllipsis
                value={
                  api?.data?.location?.headquarter
                    ? formatCompanyLocation([
                        api?.data?.location?.headquarter?.country,
                        api?.data?.location?.headquarter?.state,
                        api?.data?.location?.headquarter?.city,
                      ])
                    : 'Location Unavailable'
                }
                variant="body4"
                fontWeight={400}
                color={
                  api?.data?.location?.headquarter ? 'grey.700' : 'grey.400'
                }
              />
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>
      <Divider dashed />
      <Stack
        gap={1}
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        px={1.5}
        py={1}
      >
        {api?.isLoading ? (
          <Skeleton animation="wave" variant="text" width={126} />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <CurrentCompanyContactData iconOrder="horizontal" size="medium" />
          </Stack>
        )}
        <Typography variant="body5" fontWeight={400} color="grey.500">
          Last Update:{' '}
          <Typography
            component="span"
            variant="body4"
            fontWeight={700}
            color="grey.700"
          >
            {lastUpdateCompany}
          </Typography>
        </Typography>
      </Stack>
      <Divider />
    </Styled.Wrapper>
  );
});

CompanySummary.displayName = 'CompanySummary';
